import { Injectable } from '@angular/core';
import { ContactService } from '../../data/contact.service';
import { AppState } from '../index';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, filter, last, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { from, of } from 'rxjs';
import {
    contactDeleteFailure,
    contactDeleteRequest, contactDeleteSuccess,
    contactSaveFailure,
    contactSaveRequest, contactSaveSuccess,
    contactsTableAddAssist,
    contactsTableChangePage, contactsTableChangeQuery,
    contactsTableFailure, contactsTableRemoveAssist,
    contactsTableRequest, contactsTableSetSelection,
    contactsTableSort,
    contactsTableSuccess
} from './contacts-table.actions';
import { selectContactsTableQuery, selectContactsTableSelection } from './contacts-table.state';
import { ContactFilterService } from '../../modules/shared/services/contact-filter.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { contactEditorSaveSuccess } from '../contact-editor/contact-editor.actions';

@Injectable()
export class ContactsTableEffects {

    constructor(private actions$: Actions, private contactService: ContactService, private store: Store<AppState>,
                private snackbar: MatSnackBar, private contactFilterService: ContactFilterService,
                private dialog: MatDialog) {
    }

    contactsTableRequest$ = createEffect(() => this.actions$.pipe(
        ofType(contactsTableRequest),
        withLatestFrom(this.store.select(selectContactsTableQuery)),
        mergeMap(([ , query ]) => this.contactService.query(query).pipe(
            map((result) => {
                return contactsTableSuccess({ totalCount: result.totalItems, page: result.page });
            })
        )),
        catchError((e) => of(contactsTableFailure()))
    ));

    contactsTableFailure$ = createEffect(() => this.actions$.pipe(
        ofType(contactsTableFailure),
        tap(() => this.snackbar.open($localize`We are having difficulty communicating with the server.  Please try again later.`))
    ), { dispatch: false });

    contactsTableChangePage$ = createEffect(() => this.actions$.pipe(
        ofType(contactsTableChangePage),
        map(() => contactsTableRequest())
    ));

    contactsTableChangeSort$ = createEffect(() => this.actions$.pipe(
        ofType(contactsTableSort),
        map(() => contactsTableRequest())
    ));

    contactsTableChangeQuery$ = createEffect(() => this.actions$.pipe(
        ofType(contactsTableChangeQuery),
        map(() => contactsTableRequest())
    ));

    contactsTableFilterAdd$ = createEffect(() => this.actions$.pipe(
        ofType(contactsTableAddAssist),
        withLatestFrom(this.store.select(selectContactsTableQuery)),
        map(([ action, query ]) => this.contactFilterService.applyAssist(query, action.id, action.parameter)),
        map(query => contactsTableChangeQuery({ query }))
    ));

    contactsTableFilterRemove$ = createEffect(() => this.actions$.pipe(
        ofType(contactsTableRemoveAssist),
        withLatestFrom(this.store.select(selectContactsTableQuery)),
        map(([ action, query ]) => this.contactFilterService.removeAssist(query, action.id)),
        map(query => contactsTableChangeQuery({ query }))
    ));

    contactEditorSaveUpdateRequest = createEffect(() => this.actions$.pipe(
        ofType(contactSaveRequest),
        filter(action => !!action.contact.id),
        switchMap((action) => this.contactService.update(action.contact).pipe(
            map(() => contactSaveSuccess({ contact: action.contact })),
            catchError(() => of(contactSaveFailure()))
        ))
    ));

    contactEditorSaveNewRequest = createEffect(() => this.actions$.pipe(
        ofType(contactSaveRequest),
        filter(action => !action.contact.id),
        switchMap((action) => this.contactService.add(action.contact).pipe(
            map(() => contactSaveSuccess({ contact: action.contact })),
            catchError(() => of(contactSaveFailure()))
        ))
    ));

    contactEditorSaveSuccess = createEffect(() => this.actions$.pipe(
        ofType(contactSaveSuccess),
        tap(() => this.dialog.closeAll()),
        map(() => contactsTableRequest())
    ));

    contactEditorSaveSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(contactEditorSaveSuccess),
        tap(() => this.dialog.closeAll()),
        tap(() => this.snackbar.open($localize`Contact saved successfully.`)),
        map(() => contactsTableRequest())
    ));

    contactEditorSaveFailure = createEffect(() => this.actions$.pipe(
        ofType(contactSaveFailure),
        tap(() => this.snackbar.open($localize`Unable to save changes to this contact.  Please try again.`))
    ), { dispatch: false });

    contactDeleteRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(contactDeleteRequest),
            withLatestFrom(this.store.select(selectContactsTableSelection)),
            switchMap(([_, selections]) => {

                if (!selections || selections.length === 0) {
                    return of(contactDeleteSuccess());
                }

                return from(selections).pipe(
                    concatMap(id =>
                        this.contactService.delete(id).pipe(
                            tap(() => {
                                // IMPORTANT: do NOT catch here → error bubbles up properly
                            }),
                            map(() =>
                                contactsTableSetSelection({
                                    selection: selections = selections.filter(s => s !== id)
                                })
                            )
                        )
                    ),
                    last(),
                    map(() => contactDeleteSuccess()),
                    catchError(() => of(contactDeleteFailure()))
                );
            })
        )
    );

    contactDeleteSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(contactDeleteSuccess),
        tap(() => this.snackbar.open($localize`Contact(s) deleted successfully.`)),
        map(() => contactsTableRequest())
    ));

    contactEditorDeleteFailure = createEffect(() => this.actions$.pipe(
        ofType(contactDeleteFailure),
        tap(() => this.snackbar.open($localize`Unable to delete all the selected contacts.  Please try again.`))
    ), { dispatch: false });
}
